/**
 * The token dialog — the only UI that knows how a token is collected.
 * Registered against the auth module at startup so nothing else has to care.
 */

import type { TokenPromptReason, TokenPrompter } from "../api/auth";
import { el } from "../lib/dom";

const REASON_COPY: Readonly<Record<TokenPromptReason, string>> = {
  missing: "This app needs the API token for your vineelernote server. It is stored in this browser only.",
  rejected: "The server rejected the stored token. Enter a current one to continue.",
};

export function createTokenPrompter(): TokenPrompter {
  return async function promptForToken(reason: TokenPromptReason): Promise<string | null> {
    const input = el("input", {
      className: "input",
      attrs: {
        type: "password",
        name: "token",
        placeholder: "Bearer token",
        autocomplete: "off",
        spellcheck: false,
        required: true,
        "aria-label": "API token",
      },
    });

    const form = el("form", {
      className: "token-dialog__form",
      attrs: { method: "dialog" },
      children: [
        el("h2", { className: "token-dialog__title", text: "API token required" }),
        el("p", { className: "token-dialog__body", text: REASON_COPY[reason] }),
        input,
        el("div", {
          className: "token-dialog__actions",
          children: [
            el("button", {
              className: "button button--primary",
              attrs: { type: "submit", value: "save" },
              text: "Save token",
            }),
          ],
        }),
      ],
    });

    const dialog = el("dialog", { className: "token-dialog", children: [form] });
    document.body.appendChild(dialog);

    return new Promise<string | null>((resolve) => {
      let settled = false;
      const finish = (value: string | null): void => {
        if (settled) {
          return;
        }
        settled = true;
        dialog.close();
        dialog.remove();
        resolve(value);
      };

      form.addEventListener("submit", (event: Event) => {
        event.preventDefault();
        const value = input.value.trim();
        if (value === "") {
          input.focus();
          return;
        }
        finish(value);
      });

      // Escape closes the dialog: treated as "no token supplied".
      dialog.addEventListener("close", () => {
        finish(null);
      });
      dialog.addEventListener("cancel", (event: Event) => {
        event.preventDefault();
        finish(null);
      });

      dialog.showModal();
      input.focus();
    });
  };
}
